/* ══════════════════════════════════════════════════════════════════════
   quotationPdf.js
   Single source of truth for generating the Quotation PDF, used by both
   QuotationModal.jsx (right after creating a quotation) and LeadTable.jsx
   (when a user re-opens an existing quotation from a lead) — so the two
   surfaces always produce an identical document.
══════════════════════════════════════════════════════════════════════ */

const LOGO1_PATH = "/logo.jpeg";

const loadScript = (src) =>
  new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });

const imgToBase64 = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

export const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0 });

/* Derives { base, inclTax } for a single row from qty / rate / gst%. */
export const computeRowTotals = (row) => {
  const qty  = parseFloat(row.qty)     || 0;
  const rate = parseFloat(row.rate)    || 0;
  const gst  = parseFloat(row.gstPct ?? row.gst) || 0;
  const base = qty * rate;
  const inclTax = base + (base * gst) / 100;
  return { qty, rate, gst, base, inclTax };
};

/**
 * buildQuotationPDF
 * @param {Object} params
 * @param {string} params.branchName      - shown in place of a hardcoded Client Name
 * @param {string} params.quotationNumber
 * @param {Object} params.customer        - { companyName, address, pinCode }
 * @param {Array}  params.rows            - [{ category, item, qty, rate, gstPct }]
 * @param {string} params.subject
 * @param {string} params.employeeName
 * @param {string} params.employeePhone
 * @param {Array}  params.terms           - array of term strings (user-defined, no hardcoded defaults)
 * @param {boolean} params.forEmail
 */
export const buildQuotationPDF = async ({
  branchName,
  quotationNumber,
  customer,
  rows = [],
  subject,
  employeeName,
  employeePhone,
  terms = [],
  forEmail = false,
}) => {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");

  const [logo1] = await Promise.all([imgToBase64(LOGO1_PATH)]);

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, ml = 15, mr = W - 15;

  const computedRows = rows.map((r) => ({ ...r, ...computeRowTotals(r) }));
  const grandTotal = computedRows.reduce((s, r) => s + r.inclTax, 0);

  /* ── dark header bar — Client Name is now the lead's branch ── */
  doc.setFillColor(30, 18, 60);
  doc.rect(0, 0, W, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(branchName || "SAJ INFO Pvt. Ltd.", ml, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Registered Office: SN Mallick Rd, near Hooghly Rural Police Line, Nabapally, Daluigacha, Singur, Balarambati, West Bengal 712409", ml, 18);
  doc.text("CIN: 000000000", ml, 23);

  const imgW = 21, imgH = 18, imgY = 5;
  if (logo1) {
    try { doc.addImage(logo1, "PNG", mr - imgW, imgY, imgW, imgH); } catch (_) {}
  }

  let y = 35;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.text(
    `Date: ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })}`,
    mr, y, { align: "right" },
  );
  if (quotationNumber) {
    y += 5;
    doc.text(`Quotation No: ${quotationNumber}`, mr, y, { align: "right" });
  }
  y += 8;

  /* ── To block ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("To.", ml, y); y += 6;
  doc.setFontSize(10);
  doc.text(customer?.companyName || "—", ml, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(customer?.address || "", ml, y); y += 5;
  doc.text(`Pin: ${customer?.pinCode || ""}`, ml, y); y += 10;

  /* ── Subject ── */
  const subLine = `Sub: ${subject || "—"}`;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text(subLine, ml, y); y += 2;
  doc.setLineWidth(0.3); doc.setDrawColor(30, 18, 60);
  const lineEnd = Math.min(ml + doc.getTextWidth(subLine) + 4, mr);
  doc.line(ml, y, lineEnd, y); y += 6;

  doc.setFont("helvetica", "normal"); doc.text("Dear Sir,", ml, y); y += 5;
  const bodyText =
    "We are pleased to submit the price proposal for the below items. " +
    "The pricing is quoted in INR only with a validity of 7 days from the date of offer.";
  const bodyLines = doc.splitTextToSize(bodyText, mr - ml);
  doc.setFontSize(9);
  doc.text(bodyLines, ml, y);
  y += bodyLines.length * 5 + 4;

  /* ── items table: Category / Item / Qty / Rate / GST% / Amount(incl. tax) ── */
  doc.autoTable({
    startY: y,
    head: [["Category", "Item", "Qty", "Rate", "GST %", "Amount (Incl. Tax)"]],
    body: [
      ...computedRows.map((r) => [
        r.category || "—",
        r.item || "—",
        fmt(r.qty),
        fmt(r.rate),
        `${fmt(r.gst)}%`,
        fmt(r.inclTax),
      ]),
      ["", "", "", "", { content: "Total", styles: { fontStyle: "bold" } }, { content: fmt(grandTotal), styles: { fontStyle: "bold" } }],
    ],
    theme: "grid",
    headStyles: { fillColor: [30, 18, 60], textColor: 255, fontSize: 9, halign: "center" },
    bodyStyles: { fontSize: 8.5, textColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 30 }, 1: { cellWidth: 55 },
      2: { halign: "center", cellWidth: 15 }, 3: { halign: "center", cellWidth: 25 },
      4: { halign: "center", cellWidth: 20 }, 5: { halign: "center", cellWidth: 35 },
    },
    margin: { left: ml, right: 15 },
    styles: { lineColor: [180, 180, 200], lineWidth: 0.3 },
  });
  y = doc.lastAutoTable.finalY + 10;

  /* ── terms — fully dynamic, no hardcoded defaults ── */
  const cleanTerms = (terms || []).map((t) => (t || "").trim()).filter(Boolean);
  if (cleanTerms.length > 0) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(0);
    doc.text("Terms and Condition:", ml, y); y += 2;
    doc.line(ml, y, ml + 58, y); y += 5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
    cleanTerms.forEach((t) => {
      const tl = doc.splitTextToSize(`\u2022  ${t}`, mr - ml);
      doc.text(tl, ml, y);
      y += tl.length * 4.5 + 1.5;
    });
    y += 4;
  }

  /* ── bank details ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("Please Find The Account Details Below-", ml, y); y += 2;
  doc.line(ml, y, ml + 80, y); y += 5;
  const bankRows = [
    ["Account Number:", "0000111222111"],
    ["IFSC:",           "Demo0000000"],
    ["Bank Name:",      "Demo"],
    ["Branch:",         "Hooghly"],
    ["Account Type:",   "Current"],
    ["From:",           branchName || "SAJ INFO Pvt. Ltd."],
  ];
  doc.setFontSize(8.5);
  bankRows.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold"); doc.text(k, ml, y);
    doc.setFont("helvetica", "normal"); doc.text(v, ml + 38, y);
    y += 5;
  });
  y += 6;

  /* ── employee sign-off ── */
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text(employeeName || "—", ml, y); y += 5;
  doc.setFont("helvetica", "normal");
  if (employeePhone) doc.text(employeePhone, ml, y);

  /* ── footer ── */
  const pH = doc.internal.pageSize.height;
  doc.setDrawColor(139, 92, 246); doc.setLineWidth(0.5);
  doc.line(ml, pH - 22, mr, pH - 22);
  doc.setFontSize(7); doc.setTextColor(80); doc.setFont("helvetica", "normal");
  [
    "Registered Office: SN Mallick Rd, near Hooghly Rural Police Line, Nabapally, Daluigacha, Singur, Balarambati, West Bengal 712409",
    "Mail: info@sajinfoworld.com | PHONE:099032 14793 | www.sajinfoworld.com",
  ].forEach((fl, i) => doc.text(fl, W / 2, pH - 16 + i * 4.5, { align: "center" }));

  if (forEmail) return doc.output("datauristring");
  doc.save(`Quotation_${quotationNumber || (customer?.companyName || "Client").replace(/\s+/g, "_")}_${Date.now()}.pdf`);
  return null;
};